import { Color, Spacing } from '@persona-id/design-tokens';
import { StarFillIcon } from '@persona-id/design-tokens/icons';
import { Duration } from 'luxon';
import pluralize from 'pluralize';
import { useMemo, useState } from 'react';
import { Controller } from 'react-hook-form';
import styled from 'styled-components';

import Accordion from 'dashboard/components/Accordion';
import BiometricBadge from 'dashboard/components/BiometricBadge';
import FormControlLabel from 'dashboard/components/FormControls/FormControlLabel';
import FormError from 'dashboard/components/FormControls/FormError';
import ReactSelect from 'dashboard/components/ReactSelect';
import DurationInput from 'dashboard/components-lib/DurationInput';
import { TimeIntervalInputUnit } from 'dashboard/components-lib/DurationInput/constants';
import { convertTimeToDesiredUnit } from 'dashboard/components-lib/DurationInput/utils';
import Form from 'dashboard/layouts/FormLayout/Form';
import {
  ModalBody,
  ModalContainer,
  ModalDescription,
  ModalHeader,
} from 'dashboard/layouts/ModalLayout';
import { ENHANCED_LIST_TYPES, LIST_TYPE_NAMES } from 'dashboard/lib/constants/list';
import { List } from 'dashboard/models/list';
import Button from 'design-system/components/Button';
import Checkbox from 'design-system/components/Checkbox/Checkbox';
import Input from 'design-system/components/Input';
import Spacer from 'design-system/components/Spacer';
import Sticker from 'design-system/components/Sticker';
import useFeatureFlag from 'lib/hooks/useFeatureFlag';
import { ApiModel } from 'lib/interfaces/api';
import { useFormSecurely } from 'lib/security/reactHookForm';

interface AddListModalProps {
  hasListsEnhanced: boolean;
  inquiryTemplateFieldOptions: { label: string; value: string }[];
  handleClose: () => void;
  onSubmit: (values: AddListData) => Promise<void>;
  orgRetentionPeriodSeconds: number | null;
}

export interface AddListData {
  name: string;
  type: List['type'];
  itemsExpireAfterDays?: number;
  retentionPeriodSeconds?: number;
  allowFuzzy?: boolean;
  fieldName?: string;
}

const TypeContainer = styled.div`
  display: flex;
  gap: ${Spacing[2]};
`;

const StyledSticker = styled(Sticker)`
  height: 20px;
`;

const MAX_FACE_LIST_ITEMS_EXPIRE_AFTER_YEARS = 3;
const MAX_FACE_LIST_ITEMS_EXPIRE_AFTER_DAYS = 1095;

const AddListModal = ({
  hasListsEnhanced,
  inquiryTemplateFieldOptions,
  handleClose,
  onSubmit,
  orgRetentionPeriodSeconds,
}: AddListModalProps) => {
  const isSelfieBackgroundEnabled = useFeatureFlag('dashboard:selfie-background-list');
  const submitAndClose = (data: AddListData) => {
    onSubmit(data);
    handleClose();
  };

  const listTypeOptions = (Object.keys(LIST_TYPE_NAMES) as List['type'][])
    .filter((type) => hasListsEnhanced || type !== ApiModel.ListFace)
    .filter((type) => isSelfieBackgroundEnabled || type !== ApiModel.ListSelfieBackground)
    .sort((a, b) => a.localeCompare(b, 'en', { sensitivity: 'base' }))
    .map((listKey) => ({
      label: LIST_TYPE_NAMES[listKey as keyof typeof LIST_TYPE_NAMES],
      value: listKey,
    }));

  const formReturn = useFormSecurely<AddListData>();

  const {
    control,
    formState: { errors, isSubmitting, dirtyFields },
    handleSubmit,
    register,
    setValue,
    watch,
  } = formReturn;
  const watchedListType = watch('type');
  const faceListSelected = watchedListType === ApiModel.ListFace;
  const enhancedListSelected = ENHANCED_LIST_TYPES.includes(watchedListType);
  const orgRetentionPeriodIsSet = !!orgRetentionPeriodSeconds;
  const orgRetentionPeriodDays = convertTimeToDesiredUnit(
    orgRetentionPeriodSeconds,
    TimeIntervalInputUnit.SECONDS,
    'days',
  );
  // if org retention is set, we prepopulate retention input so it should be toggled
  const [retentionInputVisible, setRetentionInputVisible] = useState(orgRetentionPeriodIsSet);
  // if org retention is set, defaults value and unit are in days, otherwise they are in years
  const [selectedArchiveTimeUnit, setSelectedArchiveTimeUnit] = useState<TimeIntervalInputUnit>(
    orgRetentionPeriodIsSet ? TimeIntervalInputUnit.DAYS : TimeIntervalInputUnit.YEARS,
  );
  const [retentionTimeUnit, setRetentionTimeUnit] = useState<TimeIntervalInputUnit>(
    orgRetentionPeriodIsSet ? TimeIntervalInputUnit.DAYS : TimeIntervalInputUnit.YEARS,
  );
  const [archiveTimeInput, setArchiveTimeInput] = useState<number | undefined>(
    orgRetentionPeriodIsSet && orgRetentionPeriodDays
      ? Math.min(orgRetentionPeriodDays, MAX_FACE_LIST_ITEMS_EXPIRE_AFTER_DAYS)
      : 3,
  );
  const [retentionInput, setRetentionInput] = useState<number | undefined>(orgRetentionPeriodDays);

  const retentionInputRules = useMemo(() => {
    return {
      required: {
        value: retentionInputVisible,
        message: 'Please set a retention period',
      },
      min: {
        value: 1,
        message: `Must not be less than 1 ${pluralize.singular(retentionTimeUnit)}`,
      },
    };
  }, [retentionInputVisible, retentionTimeUnit]);
  const archiveTimeInputRules = useMemo(() => {
    const maxYears = faceListSelected ? MAX_FACE_LIST_ITEMS_EXPIRE_AFTER_YEARS : 10;
    const retentionInputYears = convertTimeToDesiredUnit(
      retentionInput,
      retentionTimeUnit,
      'years',
    );
    const maxDuration =
      retentionInputYears && retentionInputYears < maxYears
        ? Duration.fromObject({ [retentionTimeUnit]: retentionInput })
        : Duration.fromObject({ years: maxYears });
    const maxDurationDisplay = maxDuration.as(selectedArchiveTimeUnit);
    return {
      required: {
        value: faceListSelected,
        message: 'Please set an expiration',
      },
      max: {
        value: maxDuration.as('days'),
        message: `Must not be greater than ${maxDurationDisplay} ${selectedArchiveTimeUnit}`,
      },
      min: {
        value: 1,
        message: `Must not be less than 1 ${pluralize.singular(selectedArchiveTimeUnit)}`,
      },
    };
  }, [faceListSelected, retentionInput, retentionTimeUnit, selectedArchiveTimeUnit]);

  return (
    <ModalContainer>
      <ModalHeader onClose={handleClose}>Add new list</ModalHeader>
      <ModalDescription>
        <a
          href='https://support.withpersona.com/hc/en-us/articles/4410897998099-Lists-Overview'
          target='_blank'
          title='Learn more about Lists (Opens in a new window)'
          rel='noreferrer'
        >
          Learn more about Lists here.
        </a>
      </ModalDescription>
      <ModalBody>
        <Form onSubmit={handleSubmit(submitAndClose)}>
          <FormControlLabel htmlFor='add-list-modal__name'>Name</FormControlLabel>
          <Input
            id='add-list-modal__name'
            hasError={!!errors.name}
            {...register('name', {
              required: 'Please give your list a name',
            })}
          />
          {errors.name && <FormError>{errors.name.message}</FormError>}
          <Controller
            control={control}
            name='type'
            rules={{
              required: {
                value: true,
                message: 'Please select a type',
              },
            }}
            render={({ field: { onChange, value } }) => (
              <>
                <Spacer height={2} />
                <TypeContainer>
                  <FormControlLabel htmlFor='add-list-modal__list-type-input'>
                    Type
                  </FormControlLabel>
                  {faceListSelected && (
                    <BiometricBadge
                      dataMarketingId='biometric-badge_face-list-create'
                      stored={true}
                    />
                  )}
                  {enhancedListSelected && (
                    <StyledSticker
                      variant='primary'
                      color={Color.PrimaryBlack}
                      icon={<StarFillIcon />}
                    >
                      Enhanced
                    </StyledSticker>
                  )}
                </TypeContainer>
                <ReactSelect
                  id='add-list-modal__list-type'
                  value={value}
                  onChange={(newType: List['type']) => {
                    if (orgRetentionPeriodSeconds && !dirtyFields.retentionPeriodSeconds) {
                      setValue('retentionPeriodSeconds', orgRetentionPeriodSeconds);
                    }
                    if (newType === ApiModel.ListFace) {
                      if (orgRetentionPeriodIsSet) {
                        setValue('itemsExpireAfterDays', orgRetentionPeriodDays);
                      } else if (!dirtyFields.itemsExpireAfterDays) {
                        setValue('itemsExpireAfterDays', MAX_FACE_LIST_ITEMS_EXPIRE_AFTER_DAYS);
                      }
                    }

                    onChange(newType);
                  }}
                  options={listTypeOptions}
                  placeholder='Choose list type'
                  isSearchable={true}
                  portal={true}
                  hasError={!!errors['type']}
                />
                {errors.type && <FormError>{errors.type.message}</FormError>}
              </>
            )}
          />
          {watchedListType === ApiModel.ListName && (
            <>
              <Spacer height={2} />
              <Checkbox
                label='Allow fuzzy matching on first name'
                id='add-list-modal__allow-fuzzy-input'
                {...register('allowFuzzy')}
              />
            </>
          )}
          {watchedListType === ApiModel.ListField && (
            <Controller
              control={control}
              name='fieldName'
              rules={{ required: { value: true, message: 'Please select a field type' } }}
              render={({ field: { onChange, value } }) => (
                <>
                  <Spacer height={2} />
                  <FormControlLabel htmlFor='add-list-modal__list-field-type-input'>
                    Field Type
                  </FormControlLabel>
                  <ReactSelect
                    id='add-list-modal__list-field-type'
                    value={value}
                    onChange={onChange}
                    options={inquiryTemplateFieldOptions}
                    placeholder='Choose list type'
                    isSearchable={true}
                    portal={true}
                    hasError={!!errors['fieldName']}
                  />
                  {errors.fieldName && <FormError>{errors.fieldName.message}</FormError>}
                </>
              )}
            />
          )}
          <Spacer height={2} />
          <Accordion title='Advanced Configuration' defaultExpanded={true}>
            <Spacer height={2} />
            {faceListSelected && (
              <Controller
                control={control}
                name='itemsExpireAfterDays'
                rules={archiveTimeInputRules}
                render={({ field: { onChange } }) => (
                  <>
                    <DurationInput
                      title='Deactivate items after'
                      subtitle={
                        faceListSelected
                          ? 'Item state will change to Inactive and biometric data will be erased (max 3 years)'
                          : 'Item state will change to Inactive.'
                      }
                      hasError={!!errors.itemsExpireAfterDays}
                      onChange={(newValue) => {
                        setArchiveTimeInput(newValue.rawDuration);
                        setSelectedArchiveTimeUnit(newValue.unit);
                        onChange(newValue.convertedDuration);
                      }}
                      value={{ duration: archiveTimeInput, unit: selectedArchiveTimeUnit }}
                      desiredTimeUnit='days'
                    />
                    {errors.itemsExpireAfterDays && (
                      <FormError>{errors.itemsExpireAfterDays.message}</FormError>
                    )}
                  </>
                )}
              />
            )}

            <Spacer height={2} />
            <Controller
              control={control}
              name='retentionPeriodSeconds'
              rules={retentionInputRules}
              render={({ field: { onChange } }) => (
                <>
                  <DurationInput
                    title='Redact items after'
                    subtitle={
                      faceListSelected
                        ? 'Item data will be erased'
                        : 'Item data will be erased and item state will change to Inactive'
                    }
                    hasError={!!errors.retentionPeriodSeconds}
                    onChange={(newValue) => {
                      setRetentionInput(newValue.rawDuration);
                      setRetentionTimeUnit(newValue.unit);
                      onChange(newValue.convertedDuration);
                    }}
                    value={{ duration: retentionInput, unit: retentionTimeUnit }}
                    desiredTimeUnit='seconds'
                    onToggle={(toggleValue) => {
                      setRetentionInputVisible(toggleValue);
                      setRetentionInput(undefined);
                      setRetentionTimeUnit(TimeIntervalInputUnit.YEARS);
                      onChange(null);
                    }}
                    enableToggleByDefault={retentionInputVisible}
                  />
                  {retentionInputVisible && errors.retentionPeriodSeconds && (
                    <FormError>{errors.retentionPeriodSeconds.message}</FormError>
                  )}
                </>
              )}
            />
          </Accordion>
          <Spacer height={4} />
          <Button
            type='submit'
            width='auto'
            id='add-list-modal__btn-submit'
            isPending={isSubmitting}
          >
            Add list
          </Button>
        </Form>
      </ModalBody>
    </ModalContainer>
  );
};

export default AddListModal;
